import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  Button,
  Dropdown,
  Input,
  InputRef,
  Typography,
} from '@robinpowered/ui-kit';
import { FilterButton, FilterButtonContent } from '../styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuildings,
  faChevronDown,
  faMagnifyingGlass,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';

export interface BuildingRow {
  id: string;
  name: string;
}

interface BuildingFilterProps {
  buildings: BuildingRow[];
  loading: boolean;
  selectedLocationId: string | null;
  onChange: (id: string | null) => void;
}

export const BuildingFilter: React.FC<BuildingFilterProps> = ({
  buildings,
  loading,
  selectedLocationId,
  onChange,
}) => {
  const { t } = useTranslation('globalNavigation', {
    keyPrefix: 'searchOverlay',
  });
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (open) {
      filterInputRef.current?.focus();
    }
  }, [open]);

  const normalized = filterText.trim().toLowerCase();
  const visibleBuildings = normalized
    ? buildings.filter((building) =>
        building.name.toLowerCase().includes(normalized)
      )
    : buildings;

  const selectedBuildingName = buildings.find(
    (building) => building.id === selectedLocationId
  )?.name;

  const triggerLabel = selectedBuildingName
    ? t('buildingFilter.triggerSelected', { building: selectedBuildingName })
    : t('building');

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      popupRender={() => (
        <FilterPanel role="group" aria-label={t('buildingFilter.groupLabel')}>
          <Input
            ref={filterInputRef}
            aria-label={t('buildingFilter.searchLabel')}
            placeholder={t('buildingFilter.searchPlaceholder')}
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
            prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
          />
          <BuildingList>
            {loading && (
              <PanelMessage>{t('buildingFilter.loading')}</PanelMessage>
            )}
            {!loading && visibleBuildings.length === 0 && (
              <PanelMessage>{t('buildingFilter.empty')}</PanelMessage>
            )}
            {!loading &&
              visibleBuildings.map((building) => {
                const isSelected = building.id === selectedLocationId;
                return (
                  <BuildingOption
                    key={building.id}
                    type="text"
                    aria-pressed={isSelected}
                    onClick={() => {
                      onChange(isSelected ? null : building.id);
                      setOpen(false);
                    }}
                  >
                    {building.name}
                  </BuildingOption>
                );
              })}
          </BuildingList>
        </FilterPanel>
      )}
    >
      <FilterButton type="text">
        <FilterButtonContent>
          <FontAwesomeIcon icon={faBuildings} />
          <span>{triggerLabel}</span>
          <FontAwesomeIcon icon={faChevronDown} />
        </FilterButtonContent>
      </FilterButton>
    </Dropdown>
  );
};

const FilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 280px;
  padding: 8px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
`;

const BuildingList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 240px;
  overflow-y: auto;
`;

const BuildingOption = styled(Button)`
  justify-content: flex-start;
  width: 100%;
  min-height: 36px;
  text-align: left;

  &[aria-pressed='true'] {
    background: #f5f5f5;
    font-weight: 600;
  }
`;

const PanelMessage = styled(Typography.Text)`
  padding: 8px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 13px;
`;
